import { apiClient } from '../lib/apiClient';
import type { TestMapping, TestMappingInput, TestMappingTarget } from './testMapping';

export type ReefBotMapping = TestMapping;
export type ReefBotMappingInput = TestMappingInput;
export type ReefBotTarget = TestMappingTarget;

/**
 * Reef Kinetics ReefBot of an aquarium
 * (backend: src/routes/reefbot.ts, /api/v1/aquariums/:aquariumId/integrations/reefbot).
 * ReefGuide logs in to Reef Kinetics with the owner's account. The owner maps
 * every test of the ReefBot to a ReefGuide parameter (or "do not import"), then
 * activates the tank; its results are imported every hour as measurements.
 */

/** Outcome of the latest import, as stored by the backend. */
export interface ReefBotSyncSummary {
  at: string;
  status: 'ok' | 'failed';
  imported: number;
  updated: number;
  /** Results of tests set to "do not import". */
  skipped: number;
  /** Results of tests whose mapping is not confirmed yet. */
  unmapped: number;
  code?: string;
}

/** Never contains the password. */
export interface ReefBotConnection {
  connected: boolean;
  email?: string;
  tankId?: string | null;
  tankName?: string | null;
  lastSync?: ReefBotSyncSummary | null;
}

export interface ReefBotTank {
  id: string;
  name: string;
}

export interface ReefBotImportResult {
  connection: ReefBotConnection;
  mappings: ReefBotMapping[];
  sync: ReefBotSyncSummary;
}

export const reefbotConnectionKey = (aquariumId: string) => ['reefbot', aquariumId, 'connection'] as const;
export const reefbotTanksKey = (aquariumId: string) => ['reefbot', aquariumId, 'tanks'] as const;
export const reefbotPreviewKey = (aquariumId: string, tankId: string) => ['reefbot', aquariumId, 'preview', tankId] as const;
export const reefbotMappingsKey = (aquariumId: string) => ['reefbot', aquariumId, 'mappings'] as const;
export const reefbotTargetsKey = (aquariumId: string) => ['reefbot', aquariumId, 'targets'] as const;

function base(aquariumId: string): string {
  return `/api/v1/aquariums/${encodeURIComponent(aquariumId)}/integrations/reefbot`;
}

export async function getReefBotConnection(aquariumId: string): Promise<ReefBotConnection> {
  const res = await apiClient.get<{ data: ReefBotConnection }>(base(aquariumId));
  return res.data;
}

/** Checks the account with Reef Kinetics, then saves it. The password is never kept by this module. */
export async function connectReefBot(
  aquariumId: string,
  email: string,
  password: string,
): Promise<{ connection: ReefBotConnection; tanks: ReefBotTank[] }> {
  const res = await apiClient.put<{ data: { connection: ReefBotConnection; tanks: ReefBotTank[] } }>(
    base(aquariumId),
    { email: email.trim(), password },
  );
  return res.data;
}

export async function listReefBotTanks(aquariumId: string): Promise<ReefBotTank[]> {
  const res = await apiClient.get<{ data: ReefBotTank[] }>(`${base(aquariumId)}/tanks`);
  return res.data;
}

/** Every test of a tank's newest results with a suggested mapping, to confirm before activating. Imports nothing. */
export async function previewReefBotTank(aquariumId: string, tankId: string): Promise<ReefBotMapping[]> {
  const res = await apiClient.get<{ data: ReefBotMapping[] }>(`${base(aquariumId)}/tanks/${encodeURIComponent(tankId)}/preview`);
  return res.data;
}

/** The ReefGuide parameters a test can be mapped to, incl. the ones created for this aquarium. */
export async function listReefBotTargets(aquariumId: string): Promise<ReefBotTarget[]> {
  const res = await apiClient.get<{ data: ReefBotTarget[] }>(`${base(aquariumId)}/targets`);
  return res.data;
}

/** Saves the confirmed mapping, activates the tank and imports its results right away. */
export async function activateReefBotTank(
  aquariumId: string,
  tankId: string,
  mappings: ReefBotMappingInput[],
): Promise<ReefBotImportResult> {
  const res = await apiClient.put<{ data: ReefBotImportResult }>(`${base(aquariumId)}/tank`, { tankId, mappings });
  return res.data;
}

/** The stored mapping of the activated tank (new tests flagged 'new'). */
export async function getReefBotMappings(aquariumId: string): Promise<ReefBotMapping[]> {
  const res = await apiClient.get<{ data: ReefBotMapping[] }>(`${base(aquariumId)}/mappings`);
  return res.data;
}

/** Saves the mapping (imported results follow a change) and imports. */
export async function saveReefBotMappings(aquariumId: string, mappings: ReefBotMappingInput[]): Promise<ReefBotImportResult> {
  const res = await apiClient.put<{ data: ReefBotImportResult }>(`${base(aquariumId)}/mappings`, { mappings });
  return res.data;
}

export async function syncReefBot(
  aquariumId: string,
): Promise<{ connection: ReefBotConnection; sync: ReefBotSyncSummary }> {
  const res = await apiClient.post<{ data: { connection: ReefBotConnection; sync: ReefBotSyncSummary } }>(
    `${base(aquariumId)}/sync`,
    {},
  );
  return res.data;
}

/** Removes the account; imported measurements stay. */
export async function disconnectReefBot(aquariumId: string): Promise<void> {
  await apiClient.delete<void>(base(aquariumId));
}
